import { useEffect, useMemo } from "react";
import useSWR, { mutate } from "swr";
import type { ChangeEvent, Client } from "trailbase";
import { listNames, listRatings, listSettings } from "./api";
import type { NameRecord, RatingRecord } from "../domain/types";

const namesKey = (client: Client): readonly [string, Client] => [
  "names",
  client,
];
const ratingsKey = (client: Client): readonly [string, Client] => [
  "ratings",
  client,
];
const settingsKey = (client: Client): readonly [string, Client] => [
  "settings",
  client,
];

export type NamesState =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly error: Error }
  | { readonly status: "ready"; readonly data: readonly NameRecord[] };

export type RatingsState =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly error: Error }
  | { readonly status: "ready"; readonly data: readonly RatingRecord[] };

export type SettingsState =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly error: Error }
  | { readonly status: "ready"; readonly data: ReadonlyMap<string, string> };

export const useNames = (client: Client): NamesState => {
  const result = useSWR(namesKey(client), ([, currentClient]) =>
    listNames(currentClient),
  );
  if (result.error !== undefined) {
    return { status: "error", error: result.error };
  }
  if (result.data === undefined) {
    return { status: "loading" };
  }
  return { status: "ready", data: result.data };
};

export const useRatings = (client: Client): RatingsState => {
  const result = useSWR(ratingsKey(client), ([, currentClient]) =>
    listRatings(currentClient),
  );
  if (result.error !== undefined) {
    return { status: "error", error: result.error };
  }
  if (result.data === undefined) {
    return { status: "loading" };
  }
  return { status: "ready", data: result.data };
};

export const useSettings = (client: Client): SettingsState => {
  const result = useSWR(settingsKey(client), ([, currentClient]) =>
    listSettings(currentClient).then(
      (records) =>
        new Map(records.map((r) => [r.key, r.value])),
    ),
  );
  if (result.error !== undefined) {
    return { status: "error", error: result.error };
  }
  if (result.data === undefined) {
    return { status: "loading" };
  }
  return { status: "ready", data: result.data };
};

export const refreshNames = async (client: Client): Promise<void> => {
  await mutate(namesKey(client));
};

export const refreshRatings = async (client: Client): Promise<void> => {
  await mutate(ratingsKey(client));
};

export const refreshSettings = async (
  client: Client,
  data?: ReadonlyMap<string, string>,
): Promise<void> => {
  if (data !== undefined) {
    mutate(settingsKey(client), data, false);
  }
  await mutate(settingsKey(client));
};

export const useRatingSubscription = (client: Client): void => {
  useEffect(() => {
    let cancelled = false;
    let reader: ReadableStreamDefaultReader<ChangeEvent> | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const listen = async (): Promise<void> => {
      try {
        const stream = await client.records("ratings").subscribeAll();
        if (cancelled) {
          void stream.cancel();
          return;
        }
        reader = stream.getReader();
        while (!cancelled) {
          const result = await reader.read();
          if (result.done) {
            break;
          }
          await refreshRatings(client);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          console.warn("Rating subscription failed; will retry.", error);
          reconnectTimer = setTimeout(() => {
            if (!cancelled) {
              void listen();
            }
          }, 5000);
        }
      }
    };

    void listen();
    return () => {
      cancelled = true;
      if (reconnectTimer !== undefined) {
        clearTimeout(reconnectTimer);
      }
      void reader?.cancel();
    };
  }, [client]);
};

export const useRatingsByName = (
  ratings: readonly RatingRecord[] | undefined,
): ReadonlyMap<string, readonly RatingRecord[]> =>
  useMemo(() => {
    const grouped = new Map<string, RatingRecord[]>();
    ratings?.forEach((rating) => {
      const current = grouped.get(rating.name_id) ?? [];
      grouped.set(rating.name_id, [...current, rating]);
    });
    return grouped;
  }, [ratings]);
